/**
 * 支付 API
 * @module core/api/payment
 *
 * 注意：createOrder 需要在 POST body 中包含公共参数（后端使用 application/x-www-form-urlencoded）
 */

import type { HttpClient } from '../http/HttpClient'
import type { IDeviceInfoManager } from '../services/DeviceInfoManager'
import type {
  Package,
  CreateOrderParams,
  CreateOrderResponse,
  PaymentQRCode,
  PaymentStatusResponse,
  OrderDetail,
  ActivateCodeParams,
  ActivateCodeResponse,
  GetCouponResponse,
  Coupon,
  MiniProgramPaymentParams,
  MiniProgramPaymentResponse,
  MiniProgramVirtualPaymentParams,
  MiniProgramVirtualPaymentResponse
} from '../../types'
import { API_ENDPOINTS } from '../../constants/api-endpoints'
import { getWxLoginAndPayDomain } from '../config/SDKConfigManager'
import { stringifyQueryParams } from '../utils/url'

export class PaymentAPI {
  private deviceInfoManager?: IDeviceInfoManager

  constructor(httpClient: HttpClient, deviceInfoManager?: IDeviceInfoManager) {
    this.httpClient = httpClient
    this.deviceInfoManager = deviceInfoManager
  }

  private httpClient: HttpClient

  /** 获取套餐列表 */
  async getPackages(params?: { scene?: string | number }): Promise<Package[]> {
    const query = params?.scene
      ? `?${stringifyQueryParams({ scene: String(params.scene) })}`
      : ''
    const response = await this.httpClient.get(`${API_ENDPOINTS.PAYMENT.GET_PACKAGES}${query}`)
    return response.data.data
  }

  /** 获取 Token 套餐列表 */
  async getTokenPackages(): Promise<Package[]> {
    const response = await this.httpClient.get(API_ENDPOINTS.PAYMENT.GET_TOKEN_PACKAGES)
    return response.data.data
  }

  /** 获取积分套餐列表 */
  async getPointPackages(): Promise<Package[]> {
    const response = await this.httpClient.get(API_ENDPOINTS.PAYMENT.GET_POINT_PACKAGES)
    return response.data.data
  }

  /** 获取用户 Tokens 余额 */
  async getUserTokens(token?: string): Promise<{ tokens: number }> {
    const url = token
      ? `${API_ENDPOINTS.PAYMENT.GET_USER_TOKENS}?token=${encodeURIComponent(token)}`
      : API_ENDPOINTS.PAYMENT.GET_USER_TOKENS
    const response = await this.httpClient.get(url)
    return response.data.data
  }

  /** 获取订阅套餐列表 */
  async getSubscribePackages(): Promise<Package[]> {
    const response = await this.httpClient.get(API_ENDPOINTS.PAYMENT.GET_SUBSCRIBE_PACKAGES)
    return response.data.data
  }

  /** 取消订阅套餐 */
  async cancelSubscribe(subscribeId: string): Promise<void> {
    await this.httpClient.delete(`${API_ENDPOINTS.PAYMENT.CANCEL_SUBSCRIBE}/${subscribeId}`)
  }

  /** 创建订单 */
  async createOrder(params: CreateOrderParams): Promise<CreateOrderResponse> {
    const loginBase = getWxLoginAndPayDomain()

    // 构建请求体，需要包含公共参数（后端从 body 读取）
    let requestBody: Record<string, unknown> = {
      plan_type: params.plan_type || 0,
      goods_id: params.package_id,
      redirect_url:
        params.redirect_url || (typeof window !== 'undefined' ? window.location.href : ''),
      coupon_code: params.coupon_code || ''
    }

    // 添加公共参数到 body（与 v2 旧版保持一致）
    if (this.deviceInfoManager) {
      const commonParams = this.deviceInfoManager.getCommonParams()
      const promotionParams = this.deviceInfoManager.getPromotionParams()

      requestBody = {
        ...requestBody,
        curr_ver: commonParams.curr_ver,
        soft_id: commonParams.soft_id,
        mac_code: commonParams.mac_code,
        device_code: commonParams.device_code,
        os: commonParams.os,
        source: commonParams.source,
        device_id: commonParams.device_id,
        ekey: commonParams.ekey,
        // 推广参数（只添加非空值）
        ...(promotionParams.mp ? { mp: promotionParams.mp } : {}),
        ...(promotionParams.bdVid ? { bdVid: promotionParams.bdVid } : {}),
        ...(promotionParams.qhclickid ? { qhclickid: promotionParams.qhclickid } : {}),
        ...(promotionParams.strategy ? { strategy: promotionParams.strategy } : {})
      }
    }

    const response = await this.httpClient.post(
      API_ENDPOINTS.PAYMENT.CREATE_ORDER,
      requestBody,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  async getOrderDetail(orderId: number | string): Promise<OrderDetail> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.get(
      `${API_ENDPOINTS.PAYMENT.GET_ORDER_DETAIL}/${encodeURIComponent(String(orderId))}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  /**
   * 获取支付二维码
   * @param params - 支付参数
   * @returns 支付二维码信息
   */
  async getPaymentQRCode(params: {
    plan_type: number
    goods_id: number
    coupon_code?: string
  }): Promise<PaymentQRCode> {
    const loginBase = getWxLoginAndPayDomain()
    const queryParams = stringifyQueryParams({
      plan_type: String(params.plan_type),
      goods_id: String(params.goods_id),
      coupon_code: params.coupon_code || '',
      uncache: '1'
    })

    const response = await this.httpClient.get(
      `${API_ENDPOINTS.PAYMENT.GET_PAYMENT_QR}?${queryParams}`,
      loginBase ? { baseURL: loginBase } : undefined
    )

    const data = response.data?.data
    // 防御：后端返回业务错误（code !== 0）时 data 可能为 undefined
    if (!data) {
      const msg = response.data?.msg || response.data?.message || '获取支付二维码失败'
      throw new Error(`[PaymentAPI] ${msg}`)
    }

    return {
      order_id: String(data.order_id || data.id),
      url: data.url || '',
      url_alipay: data.url_alipay || '',
      order_url: data.order_url || '',
      order_sn: data.order_sn || '',
      qr_code_url: data.url || '',
      qr_code_url_alipay: data.url_alipay || '',
      qr_code_content: data.url || '',
      expire_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      amount: data.price || 0,
      price: data.price || 0,
      origin_price: data.origin_price || data.price || 0,
      status: data.status || 0
    }
  }

  /**
   * 检查支付状态
   * API 返回格式: { status: { code: 1, msg: "已支付" }, is_scan: 1, ... }
   * 转换为: { order_id, status: 'success' | 'pending' | 'failed', ... }
   */
  async checkPaymentStatus(orderId: string): Promise<PaymentStatusResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.get(
      `${API_ENDPOINTS.PAYMENT.CHECK_PAYMENT}?order_id=${orderId}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    const data = response.data.data

    // 转换 API 返回的 status.code 为 PaymentStatus 类型
    // status.code: 0 = 待支付, 1 = 已支付, 2 = 已取消/失败
    let status: 'pending' | 'success' | 'failed' | 'cancelled' = 'pending'
    if (data.status?.code === 1) {
      status = 'success'
    } else if (data.status?.code === 2) {
      status = 'failed'
    }

    return {
      order_id: String(data.order_id || data.id || orderId),
      status,
      paid_at: data.pay_time,
      message: data.status?.msg
    }
  }

  /** 激活码兑换 */
  async activateCode(params: ActivateCodeParams): Promise<ActivateCodeResponse> {
    const response = await this.httpClient.post(API_ENDPOINTS.PAYMENT.ACTIVATE_CODE, params)
    return response.data
  }

  /** 领取优惠券 */
  async getCoupon(couponId: number): Promise<GetCouponResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.get(
      `${API_ENDPOINTS.PAYMENT.GET_COUPON}?coupon_id=${couponId}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  /** 领取优惠券（通过 code） */
  async getCouponByCode(code: string): Promise<GetCouponResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.get(
      `${API_ENDPOINTS.PAYMENT.GET_COUPON}?code=${encodeURIComponent(code)}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  /** 获取用户优惠券列表 */
  async getUserCoupons(): Promise<Coupon[]> {
    const response = await this.httpClient.get(API_ENDPOINTS.PAYMENT.GET_USER_COUPONS)
    return response.data.data
  }

  /** 小程序支付 */
  async getMiniProgramPayment(
    params: MiniProgramPaymentParams
  ): Promise<MiniProgramPaymentResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.post(
      API_ENDPOINTS.PAYMENT.MINI_PROGRAM_PAYMENT,
      params,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  async getMiniProgramVirtualPayment(
    params: MiniProgramVirtualPaymentParams
  ): Promise<MiniProgramVirtualPaymentResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.post(
      API_ENDPOINTS.PAYMENT.MINI_PROGRAM_VIRTUAL_PAYMENT,
      params,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }
}
